import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { Holding } from '@interfaces/stats';
import { MenuItem } from '@mui/material';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import {
  type HoldingFilterOptions,
  useHoldingFilter,
} from '@utils/useHoldingFilter';
import { useContext, useRef, useState } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';

export const ACCOUNT_STATS = gql`
  query fetchAccount($accountId: SafeString!, $includeCustodianStatistics: Boolean!) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        id: stringId
        type
        custodianStatistics @include(if: $includeCustodianStatistics) {
          marketValueCents
          holdings {
            financialProduct {
              cusip
              isCash
              translatedName { ${TRANSLATED_STRING_FIELDS} }
              translatedNickName { ${TRANSLATED_STRING_FIELDS} }
            }
          }
        }
      }
    }
  }
`;

type HoldingSelectFieldProp = {
  userId: string;
  setAccountHolding: (value: Holding | null) => void;
  selectedSubAccount?: any | undefined;
  selectedHolding?: any | undefined;
  sx?: any;
  onBlur?: () => void;
  error?: boolean;
  label?: any;
  disabled?: boolean;
  options?: any;
  isSubmitted?: boolean;
  holdingFilterOptions?: HoldingFilterOptions;
};

export const HoldingSelectField = ({
  selectedSubAccount,
  selectedHolding,
  sx,
  error,
  disabled,
  label,
  onBlur,
  setAccountHolding,
  holdingFilterOptions,
  isSubmitted = false,
}: HoldingSelectFieldProp) => {
  const { translateBackendString } = useLocalization();
  const [accountHoldings, setAccountHoldings] = useState<Holding[]>([]);
  const prevSubAccountIdRef = useRef<string | undefined>();
  const { custodianConnection } = useContext(UserContext);

  // Use the custom hook to filter holdings
  const filteredHoldings = useHoldingFilter(
    accountHoldings,
    holdingFilterOptions,
  );

  const useCustodianStatistics =
    custodianConnection?.enableFetchCustodianStatistics
      ? custodianConnection?.enableFetchCustodianStatistics
      : false;

  const { loading } = useQuery(ACCOUNT_STATS, {
    skip: !selectedSubAccount?.account?.id,
    variables: {
      accountId: selectedSubAccount?.account?.id,
      includeCustodianStatistics: useCustodianStatistics,
    },
    onCompleted: (data) => {
      if (useCustodianStatistics) {
        const updatedHoldings =
          data?.fetchAccount?.account?.custodianStatistics?.holdings || [];
        setAccountHoldings(updatedHoldings);

        // Only clear holding if subAccount actually changed
        if (prevSubAccountIdRef.current !== selectedSubAccount?.account?.id) {
          setAccountHolding({});
          prevSubAccountIdRef.current = selectedSubAccount?.account?.id;
        }
      }
    },
  });

  const setHolding = (cusip: string) => {
    const found = filteredHoldings.find(
      (holding: any) => holding?.financialProduct?.cusip === cusip,
    );
    if (found) setAccountHolding(found);
  };

  const handleHoldingsSelect = (value: string) => {
    setHolding(value);
  };

  // Only show error if form is submitted
  const shouldShowError = isSubmitted && error;

  return (
    <>
      <SelectField
        fullWidth
        sx={sx}
        testId='select-holding'
        onChange={(e: any) => handleHoldingsSelect(e.target.value)}
        label={label}
        onBlur={onBlur}
        value={selectedHolding?.financialProduct?.cusip ?? ''}
        disabled={disabled || loading}
        error={shouldShowError}
      >
        {filteredHoldings.map((holding: Holding) => (
          <MenuItem
            key={holding?.financialProduct?.cusip}
            data-testid={`holding-${holding?.financialProduct?.cusip}`}
            value={holding?.financialProduct?.cusip ?? ''}
            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {translateBackendString(
              holding?.financialProduct?.translatedNickName ??
                holding?.financialProduct?.translatedName,
            )}
          </MenuItem>
        ))}
      </SelectField>
    </>
  );
};
